(function () {
  const STORAGE_KEY = 'lora_ad_click_ids';
  const LEGACY_STORAGE_KEY = 'lora_google_click_ids';
  const SYNC_KEY = 'lora_ad_click_ids_synced';
  const LEGACY_SYNC_KEY = 'lora_google_click_ids_synced';
  const EXPIRY_DAYS = 90;
  const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid'];
  const CART_ATTRIBUTE_KEYS = [
    'lora_gclid',
    'lora_gbraid',
    'lora_wbraid',
    'lora_fbclid',
    'lora_click_id_captured_at',
    'lora_click_id_landing_path',
    'lora_click_id_consent',
  ];

  let apiReady = false;
  let pendingUrlClickIds = readClickIdsFromUrl();
  let syncInFlight = null;

  function nowIso() {
    return new Date().toISOString();
  }

  function expiresAt() {
    return Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  }

  function safeJsonParse(value) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {}
  }

  function storageRemove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {}
  }

  function sanitizeClickId(value) {
    if (!value) return '';
    return String(value).trim().replace(/[^A-Za-z0-9._-]/g, '').slice(0, 250);
  }

  function landingPath() {
    return `${window.location.pathname}${window.location.search ? '?tracking_params_present=1' : ''}`;
  }

  function readClickIdsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const clickIds = {};

    CLICK_ID_KEYS.forEach((key) => {
      const value = sanitizeClickId(params.get(key));
      if (value) clickIds[key] = value;
    });

    return Object.keys(clickIds).length ? clickIds : null;
  }

  function marketingStorageAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return false;

    const marketingAllowed = privacy.marketingAllowed?.() === true;
    const saleOfDataAllowed =
      typeof privacy.saleOfDataAllowed === 'function' ? privacy.saleOfDataAllowed() === true : true;

    return marketingAllowed && saleOfDataAllowed;
  }

  function loadStoredClickIds() {
    const stored = safeJsonParse(storageGet(STORAGE_KEY)) || safeJsonParse(storageGet(LEGACY_STORAGE_KEY));
    if (!stored || !stored.ids || !stored.expires_at || stored.expires_at <= Date.now()) {
      storageRemove(STORAGE_KEY);
      storageRemove(LEGACY_STORAGE_KEY);
      storageRemove(SYNC_KEY);
      storageRemove(LEGACY_SYNC_KEY);
      return null;
    }

    return stored;
  }

  function saveClickIds(ids) {
    if (!ids || !Object.keys(ids).length) return null;

    const existing = loadStoredClickIds();
    const payload = {
      ids: {
        ...(existing?.ids || {}),
        ...ids,
      },
      captured_at: existing?.captured_at || nowIso(),
      landing_path: existing?.landing_path || landingPath(),
      expires_at: expiresAt(),
    };

    storageSet(STORAGE_KEY, JSON.stringify(payload));
    storageRemove(LEGACY_STORAGE_KEY);
    storageRemove(SYNC_KEY);
    storageRemove(LEGACY_SYNC_KEY);
    return payload;
  }

  function clearStoredClickIds() {
    storageRemove(STORAGE_KEY);
    storageRemove(LEGACY_STORAGE_KEY);
    storageRemove(SYNC_KEY);
    storageRemove(LEGACY_SYNC_KEY);
  }

  function cartAttributesFromPayload(payload) {
    return {
      lora_gclid: payload?.ids?.gclid || '',
      lora_gbraid: payload?.ids?.gbraid || '',
      lora_wbraid: payload?.ids?.wbraid || '',
      lora_fbclid: payload?.ids?.fbclid || '',
      lora_click_id_captured_at: payload?.captured_at || '',
      lora_click_id_landing_path: payload?.landing_path || '',
      lora_click_id_consent: payload ? 'marketing_granted' : '',
    };
  }

  function emptyCartAttributes() {
    return CART_ATTRIBUTE_KEYS.reduce((attributes, key) => {
      attributes[key] = '';
      return attributes;
    }, {});
  }

  function updateCartFormAttributes(payload) {
    const form = document.getElementById('cart-form');
    if (!(form instanceof HTMLFormElement)) return;

    CART_ATTRIBUTE_KEYS.forEach((key) => {
      form.querySelectorAll(`input[name="attributes[${key}]"]`).forEach((input) => input.remove());
    });

    const attributes = cartAttributesFromPayload(payload);
    Object.entries(attributes).forEach(([key, value]) => {
      if (!value) return;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `attributes[${key}]`;
      input.value = value;
      input.setAttribute('data-lora-click-id-attribute', '');
      form.appendChild(input);
    });
  }

  function cartUpdateUrl() {
    return window.Theme?.routes?.cart_update_url || '/cart/update.js';
  }

  function syncCartAttributes(options = {}) {
    if (!apiReady) return Promise.resolve(false);

    if (!marketingStorageAllowed()) {
      const hadStoredState = Boolean(
        loadStoredClickIds() || storageGet(SYNC_KEY) || storageGet(LEGACY_SYNC_KEY)
      );
      clearStoredClickIds();
      updateCartFormAttributes(null);
      if (!hadStoredState) return Promise.resolve(false);
      return postCartAttributes(emptyCartAttributes(), options).then(() => false);
    }

    const payload = pendingUrlClickIds ? saveClickIds(pendingUrlClickIds) : loadStoredClickIds();
    pendingUrlClickIds = null;

    if (!payload) {
      updateCartFormAttributes(null);
      return Promise.resolve(false);
    }

    updateCartFormAttributes(payload);

    const attributes = cartAttributesFromPayload(payload);
    const syncPayload = JSON.stringify(attributes);
    if (!options.force && storageGet(SYNC_KEY) === syncPayload) return Promise.resolve(true);

    return postCartAttributes(attributes, options).then((synced) => {
      if (synced) storageSet(SYNC_KEY, syncPayload);
      return synced;
    });
  }

  function postCartAttributes(attributes, options = {}) {
    if (syncInFlight) return syncInFlight;

    syncInFlight = fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes }),
      keepalive: options.keepalive === true,
    })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        syncInFlight = null;
      });

    return syncInFlight;
  }

  function onPrivacyReady() {
    apiReady = true;
    syncCartAttributes();
  }

  function loadCustomerPrivacyApi() {
    if (window.Shopify?.customerPrivacy) {
      onPrivacyReady();
      return;
    }

    if (!window.Shopify?.loadFeatures) return;

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      (error) => {
        if (!error && window.Shopify?.customerPrivacy) onPrivacyReady();
      }
    );
  }

  document.addEventListener('visitorConsentCollected', () => {
    syncCartAttributes({ force: true });
  });

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target?.id === 'cart-form') {
        syncCartAttributes({ keepalive: true });
      }
    },
    true
  );

  window.LoraClickIdTracking = {
    syncCartAttributes,
    getStoredClickIds: loadStoredClickIds,
  };

  loadCustomerPrivacyApi();
})();
